// Simple AES-GCM encryption utilities for client-side use
// Note: This is meant for obfuscation and convenience, not strong security

const DEFAULT_PASSPHRASE = import.meta?.env?.VITE_JOIN_LINK_SECRET || 'wiggin-join-link-secret'
const DEFAULT_SALT = 'wiggin-join-link-salt'

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

function toUrlSafeBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromUrlSafeBase64(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

async function deriveKey(passphrase: string, salt: string): Promise<CryptoKey> {
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    textEncoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  )

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: textEncoder.encode(salt),
      iterations: 100000,
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

export async function encryptString(plainText: string, passphrase = DEFAULT_PASSPHRASE, salt = DEFAULT_SALT): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKey(passphrase, salt)
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    textEncoder.encode(plainText)
  )

  // Concatenate IV + ciphertext for transport
  const combined = new Uint8Array(iv.byteLength + cipherBuffer.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(cipherBuffer), iv.byteLength)
  return toUrlSafeBase64(combined.buffer)
}

export async function decryptString(token: string, passphrase = DEFAULT_PASSPHRASE, salt = DEFAULT_SALT): Promise<string> {
  const combined = new Uint8Array(fromUrlSafeBase64(token))
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)
  const key = await deriveKey(passphrase, salt)

  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )
  return textDecoder.decode(plainBuffer)
}


